import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Storefront, ArrowRight, CheckCircle, Globe, Users, ChartLineUp,
  GraduationCap, Megaphone, MapTrifold, Handshake, UploadSimple, PaperPlaneTilt,
  CalendarBlank, Crown, MapPin, Buildings, Briefcase, Lightning, Compass, Sparkle,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { readUtms, trackConversion } from "@/lib/track";

const OPPORTUNITY_TYPES = [
  { icon: Globe, emoji: "🌍", title: "Country Partner",
    desc: "Represent Open Business Anywhere in an entire country." },
  { icon: MapTrifold, emoji: "🏙", title: "Regional Partner",
    desc: "Develop and support a state, province, or region." },
  { icon: MapPin, emoji: "🏢", title: "City Representative",
    desc: "Build local awareness and support entrepreneurs in your city." },
  { icon: Handshake, emoji: "🤝", title: "Strategic Alliance Partner",
    desc: "For consultants, accountants, lawyers, business advisors, and industry professionals." },
];

const BENEFITS = [
  { icon: Crown, text: "International Brand" },
  { icon: Globe, text: "Growing Global Network" },
  { icon: ChartLineUp, text: "Multiple Revenue Streams" },
  { icon: GraduationCap, text: "Training & Support" },
  { icon: MapTrifold, text: "Exclusive Territory Opportunities" },
  { icon: Briefcase, text: "Business Setup & Expansion Industry" },
  { icon: Lightning, text: "Long-Term Growth Potential" },
  { icon: Compass, text: "Work From Anywhere" },
];

const LOOKING_FOR = [
  "Entrepreneurs", "Business Consultants", "Immigration Professionals",
  "Accountants", "Lawyers", "Sales Professionals", "Investors",
  "Recruitment Agencies", "Business Development Experts", "Existing Business Owners",
];

const INTEREST_TYPES = [
  "Single Unit Franchise", "Multi-Unit Franchise", "Area Development Rights",
  "Master Franchise", "Country Representative", "Strategic Partnership",
  "Investment Partnership",
];
const INDUSTRIES = [
  "Food & Beverage", "Retail", "Education", "Healthcare", "Recruitment",
  "Immigration", "E-Commerce", "Consulting", "Technology", "Manufacturing",
  "Import / Export", "Other",
];
const TARGET_COUNTRIES = [
  "Canada", "UAE", "Saudi Arabia", "Indonesia", "India", "Argentina", "Other",
];
const INVESTMENT = [
  "Under $10,000", "$10,000 - $25,000", "$25,000 - $50,000",
  "$50,000 - $100,000", "$100,000 - $250,000", "$250,000+",
];
const TIMELINE = ["Immediately", "Within 3 Months", "Within 6 Months", "Within 12 Months"];

const EMPTY = {
  full_name: "", email: "", phone: "", country_of_residence: "", city: "",
  nationality: "", linkedin_url: "",
  current_occupation: "", current_business_name: "", years_of_experience: "",
  industry_experience: "", owned_business_before: "no", managed_staff_before: "no",
  interest_type: "", preferred_industry: "", target_country: "",
  target_city: "", investment_capacity: "", timeline: "",
  motivation: "", skills_resources: "", cross_promote_consent: "yes",
  selected_opportunity_slug: "",
  agreed: false,
};

export default function Franchise() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const settings = useSettings();
  const [form, setForm] = useState(EMPTY);
  const [resume, setResume] = useState(null);
  const [proposal, setProposal] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedOpp, setSelectedOpp] = useState(null);

  // Pre-fill from ?opp=slug
  useEffect(() => {
    const slug = searchParams.get("opp");
    if (!slug) return;
    api.get(`/franchise/opportunities/${slug}`)
      .then((r) => {
        setSelectedOpp(r.data);
        setForm((f) => ({
          ...f,
          selected_opportunity_slug: slug,
          preferred_industry: r.data.industry || f.preferred_industry,
          motivation: f.motivation || `I'm interested in the ${r.data.name} opportunity.`,
        }));
      })
      .catch(() => {});
  }, [searchParams]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agreed) {
      toast.error("Please accept the disclaimer to submit.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k === "agreed") fd.append("agreed", v ? "true" : "false");
        else if (v !== "" && v !== null && v !== undefined) fd.append(k, v);
      });
      if (resume) fd.append("resume", resume);
      if (proposal) fd.append("business_proposal", proposal);
      // UTM attribution
      const utm = readUtms();
      if (utm) fd.append("utm_json", JSON.stringify(utm));

      const res = await api.post("/franchise/apply", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      trackConversion("franchise_application", { value: 200 });
      toast.success("Application submitted. Reference: " + res.data.application_id.slice(0, 8));
      setForm(EMPTY);
      setResume(null); setProposal(null);
      setTimeout(() => navigate("/"), 1800);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed. Please review the required fields.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="franchise-page" className="fade-in">
      {/* Hero */}
      <section className="py-24 border-b border-gos-border" style={{ background: "var(--gos-bone)" }}>
        <div className="container-gos">
          <div className="overline mb-3 flex items-center gap-2">
            <Sparkle size={14} weight="duotone" /> Franchise &amp; Partnership Opportunities
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-gos-charcoal max-w-4xl">
            Build Something <span className="text-gos-navy">Bigger Than a Business.</span>
          </h1>
          <p className="text-gos-slate mt-5 leading-relaxed max-w-3xl text-lg">
            We&apos;re expanding globally and looking for ambitious entrepreneurs, business leaders,
            consultants, investors, and strategic partners who want to represent Open Business Anywhere
            in their country, region, or city.
          </p>
          <div className="mt-8 flex gap-3 flex-wrap">
            <a href="#apply" data-testid="franchise-hero-apply-btn" className="btn-primary">
              Apply Now <ArrowRight size={14} weight="bold" />
            </a>
            <a href="#opportunities" data-testid="franchise-hero-explore-btn" className="btn-secondary">
              Explore Opportunities
            </a>
            {settings?.interview_calendar_url && (
              <a href={settings.interview_calendar_url} target="_blank" rel="noreferrer" data-testid="franchise-hero-schedule-btn" className="btn-secondary">
                <CalendarBlank size={14} weight="bold" /> Schedule discovery call
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 border-b border-gos-border" data-testid="franchise-intro">
        <div className="container-gos max-w-3xl">
          <div className="overline mb-3">Introduction</div>
          <h2 className="font-display text-3xl lg:text-4xl text-gos-charcoal mb-6">
            The next successful territory could be <span className="text-gos-navy">yours.</span>
          </h2>
          <p className="text-gos-slate leading-relaxed mb-4">
            As Open Business Anywhere continues to expand internationally, we are
            <strong className="text-gos-charcoal"> selectively</strong> exploring partnerships with qualified
            individuals and organizations interested in helping entrepreneurs establish and grow
            businesses across multiple countries.
          </p>
          <p className="text-gos-slate leading-relaxed">
            Whether you&apos;re a consultant, accountant, immigration professional, business owner,
            investor, or entrepreneur, there may be opportunities to collaborate with us.
          </p>
        </div>
      </section>

      {/* Opportunity Types */}
      <section id="opportunities" className="py-20 border-b border-gos-border" data-testid="franchise-opportunity-types"
        style={{ background: "var(--gos-bone)" }}>
        <div className="container-gos">
          <div className="overline mb-2">Opportunity Types</div>
          <h2 className="font-display text-3xl text-gos-charcoal max-w-2xl">Four ways to partner.</h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {OPPORTUNITY_TYPES.map((o) => (
              <div key={o.title}
                data-testid={`opp-type-${o.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="bg-white border border-gos-border rounded-sm p-6 hover:border-gos-navy transition-colors group">
                <div className="text-3xl mb-3">{o.emoji}</div>
                <h3 className="font-display text-lg text-gos-charcoal mb-2">{o.title}</h3>
                <p className="text-sm text-gos-slate leading-relaxed">{o.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits — Why Partner With Us */}
      <section id="benefits" className="py-20">
        <div className="container-gos">
          <div className="overline mb-2">Why Partner With Us</div>
          <h2 className="font-display text-3xl text-gos-charcoal max-w-2xl">
            A global platform. Your local expertise.
          </h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {BENEFITS.map((b) => (
              <div
                key={b.text}
                className="bg-white border border-gos-border rounded-sm p-5 flex items-start gap-3 hover:border-gos-navy transition-colors"
                data-testid={`franchise-benefit-${b.text.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <b.icon size={22} weight="duotone" className="text-gos-navy shrink-0" />
                <div className="text-sm text-gos-charcoal">{b.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Curiosity */}
      <section className="py-20 border-y border-gos-border bg-gos-navy text-white relative overflow-hidden" data-testid="franchise-curiosity">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 25% 30%, white 1px, transparent 1px), radial-gradient(circle at 75% 70%, white 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
        <div className="container-gos max-w-3xl relative">
          <div className="overline text-white/50 mb-3">Exclusive opportunities</div>
          <h2 className="font-display text-3xl lg:text-4xl mb-5">
            Not all opportunities are <em className="not-italic text-gos-sand">publicly advertised</em>.
          </h2>
          <p className="text-white/80 leading-relaxed mb-4">
            Some territories may be available on an exclusive basis to qualified applicants.
          </p>
          <p className="text-white/80 leading-relaxed mb-6">
            Submit your application to learn what opportunities may be available in
            <strong className="text-white"> your country, region, or city.</strong>
          </p>
          <a href="#apply" data-testid="franchise-curiosity-cta" className="btn-sand">
            Find out what&apos;s available in your market <ArrowRight size={14} weight="bold" />
          </a>
        </div>
      </section>

      {/* Who we are looking for */}
      <section className="py-20" data-testid="franchise-looking-for">
        <div className="container-gos">
          <div className="overline mb-2">Who We&apos;re Looking For</div>
          <h2 className="font-display text-3xl text-gos-charcoal max-w-2xl mb-10">
            You don&apos;t need a franchise background — you need ambition, network, and credibility.
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {LOOKING_FOR.map((role) => (
              <div key={role}
                data-testid={`franchise-role-${role.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="bg-white border border-gos-border rounded-sm px-4 py-3 text-sm text-gos-charcoal hover:border-gos-navy transition-colors text-center">
                {role}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application CTA blurb */}
      <section className="py-12 border-t border-gos-border" data-testid="franchise-cta-blurb">
        <div className="container-gos max-w-3xl">
          <div className="overline mb-3">Interested in learning more?</div>
          <p className="text-gos-slate leading-relaxed mb-3">
            Complete the application form below and our team will review your profile, experience, location, and business objectives.
          </p>
          <p className="text-gos-charcoal leading-relaxed font-medium">
            Qualified applicants may be invited to discuss available partnership opportunities.
          </p>
        </div>
      </section>

      {/* Application Form */}
      <section id="apply" className="py-20 border-t border-gos-border" style={{ background: "var(--gos-bone)" }}>
        <div className="container-gos grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="overline mb-2">Application</div>
            <h2 className="font-display text-3xl text-gos-charcoal">Franchise Application Form</h2>
            <p className="text-gos-slate mt-4 text-sm leading-relaxed">
              Tell us about yourself, your business background and the opportunity you&apos;re interested in.
              We&apos;ll review and reach out if there&apos;s a strong fit.
            </p>
            <ul className="mt-6 space-y-2 text-xs text-gos-muted">
              <li>✓ ~5 minutes to complete</li>
              <li>✓ Documents optional (resume / business proposal)</li>
              <li>✓ Confidential — reviewed by our partnership team</li>
            </ul>
          </div>

          <form
            onSubmit={submit}
            data-testid="franchise-form"
            className="lg:col-span-8 bg-white border border-gos-border rounded-sm p-8 space-y-8"
          >
            {selectedOpp && (
              <div className="bg-gos-navy/5 border border-gos-navy/20 rounded-sm p-4 flex items-start gap-3" data-testid="selected-opp-banner">
                <Storefront size={20} weight="duotone" className="text-gos-navy shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="overline">Applying for</div>
                  <div className="font-display text-lg text-gos-charcoal">{selectedOpp.name}</div>
                  {selectedOpp.tagline && <div className="text-xs text-gos-slate mt-0.5">{selectedOpp.tagline}</div>}
                </div>
                <button type="button" onClick={() => { setSelectedOpp(null); setForm((f) => ({ ...f, selected_opportunity_slug: "" })); }}
                  className="text-xs text-gos-slate hover:text-rose-600">Remove</button>
              </div>
            )}
            {/* Basic */}
            <Group title="Basic Information">
              <Field label="Full Name" required value={form.full_name} onChange={(v) => upd("full_name", v)} testid="fr-full-name" />
              <Field label="Email Address" type="email" required value={form.email} onChange={(v) => upd("email", v)} testid="fr-email" />
              <Field label="Mobile / WhatsApp Number" required value={form.phone} onChange={(v) => upd("phone", v)} testid="fr-phone" />
              <Field label="Country of Residence" required value={form.country_of_residence} onChange={(v) => upd("country_of_residence", v)} testid="fr-country-residence" />
              <Field label="City" required value={form.city} onChange={(v) => upd("city", v)} testid="fr-city" />
              <Field label="Nationality" required value={form.nationality} onChange={(v) => upd("nationality", v)} testid="fr-nationality" />
              <Field label="LinkedIn Profile" placeholder="https://linkedin.com/in/…" value={form.linkedin_url} onChange={(v) => upd("linkedin_url", v)} testid="fr-linkedin" />
            </Group>

            {/* Business */}
            <Group title="Business Information">
              <Field label="Current Occupation" value={form.current_occupation} onChange={(v) => upd("current_occupation", v)} testid="fr-occupation" />
              <Field label="Current Business Name" value={form.current_business_name} onChange={(v) => upd("current_business_name", v)} testid="fr-business-name" />
              <Field label="Years of Business Experience" value={form.years_of_experience} onChange={(v) => upd("years_of_experience", v)} placeholder="e.g. 7 years" testid="fr-years" />
              <Field label="Industry Experience" value={form.industry_experience} onChange={(v) => upd("industry_experience", v)} placeholder="e.g. SaaS, F&B" testid="fr-industry-exp" />
              <YesNo label="Have you owned a business before?" value={form.owned_business_before} onChange={(v) => upd("owned_business_before", v)} testid="fr-owned" />
              <YesNo label="Have you managed staff before?" value={form.managed_staff_before} onChange={(v) => upd("managed_staff_before", v)} testid="fr-managed" />
            </Group>

            {/* Franchise Interest */}
            <Group title="Franchise Interest">
              <Select label="Which opportunity are you interested in?" required value={form.interest_type} onChange={(v) => upd("interest_type", v)} testid="fr-interest">
                <option value="">— Select —</option>
                {INTEREST_TYPES.map((t) => <option key={t}>{t}</option>)}
              </Select>
              <Select label="Preferred Industry" required value={form.preferred_industry} onChange={(v) => upd("preferred_industry", v)} testid="fr-pref-industry">
                <option value="">— Select —</option>
                {INDUSTRIES.map((t) => <option key={t}>{t}</option>)}
              </Select>
              <Select label="Target Country" required value={form.target_country} onChange={(v) => upd("target_country", v)} testid="fr-target-country">
                <option value="">— Select —</option>
                {TARGET_COUNTRIES.map((t) => <option key={t}>{t}</option>)}
              </Select>
              <Field label="Target City / Region" value={form.target_city} onChange={(v) => upd("target_city", v)} testid="fr-target-city" />
              <Select label="Investment Capacity" required value={form.investment_capacity} onChange={(v) => upd("investment_capacity", v)} testid="fr-investment">
                <option value="">— Select —</option>
                {INVESTMENT.map((t) => <option key={t}>{t}</option>)}
              </Select>
              <Select label="Timeline" required value={form.timeline} onChange={(v) => upd("timeline", v)} testid="fr-timeline">
                <option value="">— Select —</option>
                {TIMELINE.map((t) => <option key={t}>{t}</option>)}
              </Select>
            </Group>

            {/* Additional */}
            <Group title="Additional Information" cols={1}>
              <Textarea label="Why are you interested in a franchise opportunity?" value={form.motivation} onChange={(v) => upd("motivation", v)} rows={3} testid="fr-motivation" />
              <Textarea label="What skills, experience, or resources can you bring?" value={form.skills_resources} onChange={(v) => upd("skills_resources", v)} rows={3} testid="fr-skills" />
              <FileField label="Upload Resume or Business Profile" onChange={setResume} value={resume} testid="fr-resume" />
              <FileField label="Upload Business Proposal (Optional)" onChange={setProposal} value={proposal} testid="fr-proposal" />
              <YesNo
                label="Would you like us to contact you regarding other business, investment, immigration, or expansion opportunities?"
                value={form.cross_promote_consent} onChange={(v) => upd("cross_promote_consent", v)} testid="fr-cross-promote"
              />
            </Group>

            <label className="flex items-start gap-3 text-xs text-gos-slate" data-testid="fr-agreement-label">
              <input
                type="checkbox" data-testid="fr-agreement"
                checked={form.agreed} onChange={(e) => upd("agreed", e.target.checked)}
                className="mt-1 accent-gos-navy"
              />
              <span>
                I understand that submitting this application does not guarantee approval or the award of franchise rights.
              </span>
            </label>

            <button
              type="submit" disabled={submitting} data-testid="fr-submit-btn"
              className="btn-primary w-full"
            >
              {submitting ? "Sending…" : (<>
                <PaperPlaneTilt size={14} weight="bold" /> Submit Franchise Application
              </>)}
            </button>
            <p className="text-[10px] text-gos-muted text-center">
              Your information is treated confidentially. Files limited to 10 MB each.
            </p>
          </form>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-10 border-t border-gos-border" data-testid="franchise-disclaimer">
        <div className="container-gos max-w-3xl">
          <div className="overline mb-2">Disclaimer</div>
          <p className="text-xs text-gos-slate leading-relaxed italic">
            Submission of an application does not guarantee approval, partnership rights, territorial exclusivity,
            or franchise rights. Opportunities are offered at the sole discretion of Open Business Anywhere and may
            vary by location and market conditions.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 border-t border-gos-border bg-gos-navy text-white relative overflow-hidden" data-testid="franchise-final-cta">
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{ backgroundImage: "linear-gradient(135deg, transparent 49%, white 49%, white 51%, transparent 51%)", backgroundSize: "40px 40px" }} />
        <div className="container-gos text-center max-w-3xl mx-auto relative">
          <h2 className="font-display text-4xl lg:text-5xl tracking-tight">
            Your City. Your Region. <span className="text-gos-sand">Your Country.</span>
          </h2>
          <p className="font-display text-2xl mt-4 text-white/85">
            One opportunity could change everything.
          </p>
          <a href="#apply" data-testid="franchise-final-cta-btn" className="btn-sand mt-8 inline-flex">
            Apply Today <ArrowRight size={14} weight="bold" />
          </a>
        </div>
      </section>
    </div>
  );
}

function Group({ title, cols = 2, children }) {
  return (
    <div>
      <div className="overline mb-3">{title}</div>
      <div className={`grid grid-cols-1 ${cols === 2 ? "md:grid-cols-2" : ""} gap-4`}>
        {children}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, placeholder, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-gos-muted mb-1">
        {label}{required && " *"}
      </label>
      <input
        data-testid={testid} type={type} value={value} required={required} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gos-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gos-navy"
      />
    </div>
  );
}

function Select({ label, value, onChange, required, testid, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-gos-muted mb-1">
        {label}{required && " *"}
      </label>
      <select
        data-testid={testid} value={value} required={required}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gos-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gos-navy bg-white"
      >
        {children}
      </select>
    </div>
  );
}

function Textarea({ label, value, onChange, rows = 3, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-gos-muted mb-1">{label}</label>
      <textarea
        data-testid={testid} value={value} rows={rows}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gos-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gos-navy"
      />
    </div>
  );
}

function YesNo({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-gos-muted mb-1">{label}</label>
      <div className="flex gap-2" data-testid={testid}>
        {["yes", "no"].map((opt) => (
          <button
            key={opt} type="button" onClick={() => onChange(opt)}
            data-testid={`${testid}-${opt}`}
            className={`px-4 py-2 text-xs uppercase tracking-widest rounded-sm border transition-colors ${
              value === opt ? "bg-gos-navy text-white border-gos-navy" : "border-gos-border text-gos-slate hover:border-gos-navy"
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

function FileField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-gos-muted mb-1">{label}</label>
      <label className="flex items-center gap-3 border border-dashed border-gos-border rounded-sm px-3 py-3 text-sm text-gos-slate cursor-pointer hover:border-gos-navy">
        <UploadSimple size={18} weight="duotone" />
        <span className="flex-1 truncate">{value ? value.name : "Click to choose a file (PDF, DOC, JPG — up to 10 MB)"}</span>
        <input
          data-testid={testid} type="file" className="hidden"
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          onChange={(e) => onChange(e.target.files?.[0] || null)}
        />
      </label>
    </div>
  );
}
